import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MaCircleButton, MaIcon, MaImageSlot } from "../components/ui";
import { useArticle } from "../lib/queries";
import { sanitizeHtml } from "../lib/sanitize";

// Reader for /eat/:id. Renders the sanitized <content:encoded> from RSS
// inline; "Read on <source> ↗" link at the bottom credits the source.
// Same public-access pattern as /event/:id (no <RequireAuth>).

export function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: article, isPending, isError } = useArticle(id);

  // Sanitize once per body change so the regex sweep doesn't re-run on
  // every keystroke / scroll. memoized by reference to the raw html.
  const safeHtml = useMemo(() => sanitizeHtml(article?.bodyHtml ?? ""), [article?.bodyHtml]);

  if (isPending) {
    return (
      <div className="ma-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", letterSpacing: "0.16em" }}>loading…</span>
      </div>
    );
  }

  if (isError || !article) {
    return (
      <div className="ma-screen" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div className="ma-display" style={{ fontSize: 26, marginBottom: 12, textAlign: "center" }}>Article not found</div>
        <button onClick={() => navigate("/eat")} style={{
          background: "var(--ma-ink)", color: "var(--ma-paper)", border: "none",
          padding: "12px 24px", borderRadius: "var(--ma-card-radius)",
          fontFamily: "var(--ma-mono)", fontSize: 11, letterSpacing: "0.12em",
          textTransform: "uppercase", cursor: "pointer",
        }}>
          Back to EAT!
        </button>
      </div>
    );
  }

  // Friendly source label — pull domain from URL for the "Read on" line.
  let sourceHost = article.source;
  try { sourceHost = new URL(article.url).hostname.replace(/^www\./, ""); } catch { /* keep id */ }

  const published = article.publishedAt
    ? new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Amsterdam",
        weekday: "short", day: "numeric", month: "short", year: "numeric",
      }).format(new Date(article.publishedAt * 1000))
    : null;

  return (
    <div className="ma-screen" style={{ overflowY: "auto", paddingBottom: 110 }}>
      <div style={{ position: "relative", height: 280, overflow: "hidden" }}>
        <MaImageSlot height={280} tone="mustard" src={article.imageUrl ?? null} />
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(to bottom, rgba(20,12,4,0.5) 0%, transparent 30%, transparent 60%, rgba(20,12,4,0.6) 100%)",
        }} />
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0,
          padding: "calc(env(safe-area-inset-top, 20px) + 30px) 14px 0",
        }}>
          <MaCircleButton icon="back" label="Back" onClick={() => navigate(-1)} />
        </div>
      </div>

      <div style={{
        background: "var(--ma-paper)", padding: "22px 22px 16px",
        marginTop: -16, borderTopLeftRadius: 22, borderTopRightRadius: 22,
        position: "relative", zIndex: 2,
      }}>
        <div className="ma-mono" style={{
          fontSize: 10, letterSpacing: "0.18em",
          textTransform: "uppercase", color: "var(--ma-brick)", marginBottom: 8,
        }}>
          eat! · {article.source}
        </div>
        <div className="ma-display" style={{ fontSize: 30, color: "var(--ma-ink)", lineHeight: 1.05, marginBottom: 10 }}>
          {article.title}
        </div>
        <div className="ma-mono" style={{ fontSize: 11, letterSpacing: "0.04em", color: "var(--ma-ink-soft)" }}>
          {[article.author, published].filter(Boolean).join(" · ")}
        </div>
      </div>

      <div
        className="ma-article-body"
        style={{
          padding: "16px 22px 8px",
          fontFamily: "var(--ma-body)", fontSize: 16, lineHeight: 1.55,
          color: "var(--ma-ink)",
        }}
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />

      <div style={{ padding: "16px 22px 0" }}>
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 18px",
            background: "var(--ma-ink)", color: "var(--ma-paper)",
            border: "var(--ma-border) solid var(--ma-ink)",
            borderRadius: "var(--ma-card-radius)",
            textDecoration: "none",
          }}
        >
          <div>
            <div className="ma-mono" style={{ fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", opacity: 0.7, marginBottom: 2 }}>
              read on
            </div>
            <div style={{ fontFamily: "var(--ma-display)", fontWeight: 700, fontSize: 16 }}>
              {sourceHost}
            </div>
          </div>
          <MaIcon name="arrow" size={18} stroke={2.2} color="var(--ma-paper)" />
        </a>
      </div>
    </div>
  );
}
